import { memo, useEffect, useRef, useState } from 'react';
import type { PlaylistItem } from '../types.ts';
import { Chevron } from './Chevron.tsx';

interface Props {
  items: PlaylistItem[];
  currentIndex: number;
  onJump: (index: number) => void;
  disabled?: boolean;
  /** When true, clips can be dragged to reorder them (admin only). */
  reorderable?: boolean;
  /** Called with the new shot-id order after a drag. */
  onReorder?: (shotIds: string[]) => void;
  /** Strip visible; when false only the collapse arrow remains. */
  open?: boolean;
  onToggle?: () => void;
}

/**
 * The shot strip: one thumbnail per version in the playlist, coloured by review status, with
 * the current shot highlighted. This is how a dailies session navigates — click or arrow
 * through shots without leaving the player. When `reorderable`, clips can be dragged into a
 * new order (native HTML5 drag, no library).
 */
function TimelineImpl({
  items,
  currentIndex,
  onJump,
  disabled,
  reorderable,
  onReorder,
  open = true,
  onToggle,
}: Props): React.JSX.Element {
  const activeRef = useRef<HTMLButtonElement | null>(null);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [overIndex, setOverIndex] = useState<number | null>(null);

  // Keep the current shot visible when the room jumps somewhere far down the list.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [currentIndex]);

  const resetDrag = (): void => {
    setDragIndex(null);
    setOverIndex(null);
  };

  const drop = (to: number): void => {
    const from = dragIndex;
    resetDrag();
    if (from === null || from === to) return;
    const shots = items.map((it) => it.version.shotId);
    const [moved] = shots.splice(from, 1);
    if (moved === undefined) return;
    shots.splice(to, 0, moved);
    onReorder?.(shots);
  };

  return (
    <div className={`timeline${open ? '' : ' timeline--collapsed'}`}>
      {onToggle ? (
        <button
          type="button"
          className="timeline__toggle"
          onClick={onToggle}
          title={open ? 'Collapse clips' : 'Show clips'}
          aria-label={open ? 'Collapse clips' : 'Show clips'}
        >
          <Chevron dir={open ? 'down' : 'up'} />
        </button>
      ) : null}
      {/* Stays mounted when collapsed: the strip needs a height to animate away from, and
          unmounting it is what made the panel jump. Hidden from assistive tech and the tab
          order while it is closed, so it is not reachable when it cannot be seen. */}
      <div className="timeline__reveal" inert={!open || undefined}>
        <div className="timeline__reveal-inner">
      <div className="timeline__strip">
        {items.map((item, index) => {
          const active = index === currentIndex;
          return (
            <button
              key={item.itemId}
              ref={active ? activeRef : undefined}
              type="button"
              disabled={disabled}
              draggable={reorderable}
              className={
                `shot${active ? ' shot--active' : ''} shot--${item.version.status}` +
                `${reorderable ? ' shot--draggable' : ''}${overIndex === index && dragIndex !== null ? ' shot--dragover' : ''}`
              }
              onClick={() => onJump(index)}
              onDragStart={(e) => {
                if (!reorderable) return;
                setDragIndex(index);
                e.dataTransfer.effectAllowed = 'move';
                e.dataTransfer.setData('text/plain', String(index));
              }}
              onDragOver={(e) => {
                if (!reorderable || dragIndex === null) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (overIndex !== index) setOverIndex(index);
              }}
              onDrop={(e) => {
                if (!reorderable) return;
                e.preventDefault();
                drop(index);
              }}
              onDragEnd={resetDrag}
              title={`${item.version.label} — ${item.version.status}`}
            >
              {item.version.media.poster ? (
                <img src={item.version.media.poster} alt="" loading="lazy" draggable={false} />
              ) : (
                <span className="shot__placeholder">{item.version.ingestState}</span>
              )}
              <span className="shot__name">{item.version.shotName}</span>
              {item.version.counts.comments > 0 ? (
                <span className="shot__badge">{item.version.counts.comments}</span>
              ) : null}
            </button>
          );
        })}
      </div>
        </div>
      </div>
    </div>
  );
}

/**
 * The clip strip is a poster image per clip and never depends on the playhead, so it has no
 * business re-rendering with it.
 */
export const Timeline = memo(TimelineImpl);
